import { useContext, useEffect, useMemo, useState } from 'react';

import { Button, Checkbox, Divider, Flex, Grid, Text, useDisclosure } from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import { toast } from 'react-toastify';

import { Deal } from '../../../../gql/dealGql';
import {
  Bank,
  ConsentNameEnumType,
  DealInput,
  LendingNetwork,
  useSubmitToProvidersMutation,
} from '../../../../gql/generated/graphql';

import Modal from '../../../shared/Modal';
import Tooltip from '../../../shared/Tooltip';
import NoHardConsentModal from '../modals/NoHardConsentModal';

import { PermissionEnum } from '../../../../constants/permissions';
import { Can } from '../../../../libs/Can';
import { DealActionsEnum, DealContext } from '../../../../libs/DealContext';
import { isNumber } from '../../../../libs/utils';
import { cleanDealForUpdate } from '../../../../utils/deals';

const canSubmitCreditApp = (potentialLender: Bank): potentialLender is LenderOptions =>
  !!potentialLender.name && !!potentialLender.provider_lender_id && !!potentialLender.provider;

type LenderOptions = {
  name: string;
  provider_lender_id: string;
  provider: LendingNetwork;
  selected?: boolean;
};

const mapBankToLender = ({ name, provider_lender_id, provider }: LenderOptions): LenderOptions => ({
  name,
  provider_lender_id,
  provider,
  selected: false,
});

type LendersByProviderProps = {
  lenderOptions: LenderOptions[];
  lendersOptionsByProvider: LenderOptions[];
  setLenderOptions: (lenderOptions: LenderOptions[]) => void;
  title: string;
};

const LendersByProvider = ({
  lenderOptions,
  lendersOptionsByProvider,
  setLenderOptions,
  title,
}: LendersByProviderProps) => {
  if (lendersOptionsByProvider.length === 0) {
    return null;
  }

  return (
    <>
      <Flex align="center" gap={2} mb={1}>
        <Text fontSize="lg" fontWeight="bold">
          {title}
        </Text>
      </Flex>
      <Divider mb={2} />
      <Grid templateColumns="repeat(2, 1fr)" gap={2} mb={4}>
        {lendersOptionsByProvider.map((l) => {
          // Adding the `name` to the logic because, in staging, there are duplicated `provider_lender_ids`.
          const index = lenderOptions.findIndex(
            (option) =>
              option.provider_lender_id === l.provider_lender_id && option.name === l.name,
          );
          if (index === -1) {
            return null;
          }

          return (
            <Checkbox
              key={`${l.provider_lender_id}-${l.name}`}
              isChecked={l.selected}
              onChange={() => {
                const newLenderOptions = [...lenderOptions];
                newLenderOptions[index] = { ...l, selected: !l.selected };
                setLenderOptions(newLenderOptions);
              }}
              fontWeight="normal"
            >
              {l.name}
            </Checkbox>
          );
        })}
      </Grid>
    </>
  );
};

type SubmitCreditAppButtonProps = {
  banks: Bank[];
};

const SubmitCreditAppButton = ({ banks }: SubmitCreditAppButtonProps) => {
  const { isRecalculatingPayoff, deal, dispatch } = useContext(DealContext);

  const hasHardConsent = deal.consents?.some(
    (consent) => consent.type && consent.type === ConsentNameEnumType.CreditScoreHardPull,
  );

  const { values, setFieldValue, errors } = useFormikContext<Deal>();

  const [lenderOptions, setLenderOptions] = useState<LenderOptions[]>([]);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const noHardConsentModalProps = useDisclosure();

  const cudlFallbackEligibleBanksLabel = useMemo(
    () =>
      banks
        .filter((bank) => bank.provider === LendingNetwork.Cudl)
        .map((bank) => bank.name)
        .filter((bankName): bankName is string => bankName != null)
        .join(' & '),
    [banks],
  );

  const [submitToProviders, { loading }] = useSubmitToProvidersMutation({
    onCompleted: (data) => {
      setFieldValue('financial_info.id', data.submitToProviders?.financial_info?.id);
      setFieldValue('has_funding_jacket', data.submitToProviders?.has_funding_jacket);

      toast.success('Credit App Submitted');
    },
    onError: (e) => {
      toast.error(`${e}`, { autoClose: false });
    },
  });

  useEffect(
    () =>
      setLenderOptions(
        banks
          .filter(canSubmitCreditApp)
          .map(mapBankToLender)
          .sort((a, b) => a.name.localeCompare(b.name)),
      ),
    [banks],
  );

  const groupedLenderOptions = useMemo(
    () =>
      lenderOptions.reduce(
        (acc, lender) => {
          acc[lender.provider] = [...acc[lender.provider], lender];
          return acc;
        },
        { R1: [], CUDL: [] } as Record<LendingNetwork, LenderOptions[]>,
      ),
    [lenderOptions],
  );

  useEffect(() => {
    setIsDisabled(
      !values.car.year ||
        !values.car.vin ||
        !values.car.mileage ||
        !isNumber(values.car.retail_book_value) ||
        !isNumber(values.car.payoff.vehicle_payoff) ||
        !values.customer.address?.state ||
        !!errors.car?.vehicle_type ||
        !!errors.car?.payoff?.vehicle_payoff,
    );
  }, [values.customer.address, values.car, values.financial_info]);

  const handleOnOpen = () => {
    if (!hasHardConsent) {
      noHardConsentModalProps.onOpen();
    } else {
      onOpen();
    }
  };

  const handleOnClose = () => {
    setLenderOptions(
      lenderOptions.map((l) => ({
        ...l,
        selected: false,
      })),
    );
    onClose();
  };

  const submitToLenders = async () => {
    // The funding jacket is resolved server-side from deal_jackets (LE-18574);
    // submitToProviders ignores any r1_jacket_id sent on the input, so there is no
    // jacket id to echo back to avoid creating a new jacket (LE-18665).
    const cleanDeal = cleanDealForUpdate(values);

    try {
      const response = await submitToProviders({
        variables: {
          // TODO: temporary conversion. We should remove all classes and use the generated classes.
          deal: cleanDeal as DealInput,
          lenderIds: lenderOptions.filter((l) => l.selected).map((l) => l.provider_lender_id),
        },
      });

      handleOnClose();

      if (response.data?.submitToProviders) {
        const { has_funding_jacket } = response.data?.submitToProviders;

        // Sync the jacket-presence gate into deal state so signing can proceed immediately.
        dispatch({
          type: DealActionsEnum.UpdateDeal,
          payload: {
            has_funding_jacket: has_funding_jacket ?? undefined,
          },
        });
      }
    } catch (e) {
      toast.error(`${e}`);
    }
  };

  return (
    <Can I={PermissionEnum.SubmitCreditApplication}>
      <Tooltip errors={[errors.car?.vehicle_type, errors.car?.payoff?.vehicle_payoff]}>
        <Button
          variant="secondary"
          float="right"
          isLoading={loading}
          loadingText="SUBMIT CREDIT APP"
          isDisabled={isDisabled || isRecalculatingPayoff}
          onClick={handleOnOpen}
        >
          Submit Credit App
        </Button>
      </Tooltip>
      <Modal
        size="2xl"
        title="Lender Selection"
        isOpen={isOpen}
        canDismiss={!loading}
        onClose={handleOnClose}
        leftButtons={
          <Button onClick={handleOnClose} variant="warning" isDisabled={loading}>
            CANCEL
          </Button>
        }
        rightButtons={
          <Button
            onClick={submitToLenders}
            isLoading={loading}
            isDisabled={!lenderOptions.some((l) => l.selected)}
          >
            SUBMIT
          </Button>
        }
      >
        <LendersByProvider
          lenderOptions={lenderOptions}
          lendersOptionsByProvider={groupedLenderOptions.R1}
          setLenderOptions={setLenderOptions}
          title="Route One Banks"
        />
        <LendersByProvider
          lenderOptions={lenderOptions}
          lendersOptionsByProvider={groupedLenderOptions.CUDL}
          setLenderOptions={setLenderOptions}
          title="CUDL Banks"
        />

        {/* todo: Remove this once we add the CUDL lenders and the banks appear in the list. */}
        {/* If there are CUDL banks but no grouped CUDL options, none have provider_lender_id yet. */}
        {cudlFallbackEligibleBanksLabel && groupedLenderOptions.CUDL.length === 0 ? (
          <Text my={2}>
            NOTE: This deal is eligible for {cudlFallbackEligibleBanksLabel}. Submit application
            through CUDL.
          </Text>
        ) : null}
      </Modal>
      {!hasHardConsent && <NoHardConsentModal {...noHardConsentModalProps} />}
    </Can>
  );
};

export default SubmitCreditAppButton;
